import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Button, Grid, CircularProgress, CssBaseline } from '@mui/material';
import { httpsCallable } from 'firebase/functions';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import ExternalNarratorDataFieldsSection from '../../components/ExternalNarratorDataFieldsSection/ExternalNarratorDataFieldsSection';
import { updateQuickTale, uploadQuickTaleImageToStorage, uploadQuickTaleAudioToStorage } from '../../hooks/QuickTalesHooks';
import { updateBatchQuickTale } from '../../../OrganizationBatches/hooks/BatchQuickTalesHooks';
import { getCustomNarratorByName, getCustomNarratorByNameAndOrganization } from '../../hooks/CustomNarratorsHooks';
import { CenteredToast } from '../../../../common/alert';
import { showNotification, showNotificationCenter } from "../../../../constants";
import { functions, db, OrgEmailTemplatesRef } from '../../../../firebase';
import { getSingleOrg } from '../../../OrgProf/hook/Organizationhook';
import AudioModal from '../../../../common/AudioModal';
import ImagePreviewModal from '../../../../common/ImagePreviewModal';
import { ensureFirebaseAuth } from '../../../../utils/ensureFirebaseAuth';

const getNarratorDefaultsFromTemplate = (template) => {
  return {
    narratorCanUpdateAudio:
      typeof template?.narratorCanUpdateAudio === 'boolean'
        ? template.narratorCanUpdateAudio
        : true,
    narratorCanUpdatePhoto:
      typeof template?.narratorCanUpdatePhoto === 'boolean'
        ? template.narratorCanUpdatePhoto
        : false,
    narratorCanUpdateTitle:
      typeof template?.narratorCanUpdateTitle === 'boolean'
        ? template.narratorCanUpdateTitle
        : false,
    narratorCanUpdateDescription:
      typeof template?.narratorCanUpdateDescription === 'boolean'
        ? template.narratorCanUpdateDescription
        : false,
    narratorGreeting: template?.narratorGreeting || '',
  };
};

/**
 * Resolve narrator token values according to QuickTales rules:
 * - If quicktale.narrator is a user id, use quicktale.narratorData fields.
 * - If quicktale.narrator is a custom narrator name, fetch customNarrator by
 *   (name == quicktale.narrator, createdBy == quicktale.createdBy)
 */
const resolveNarratorTokens = async (quicktale) => {
  // Case 1: narrator is a registered user (server provided narratorData)
  if (quicktale?.narratorData) {
    return {
      firstName: quicktale.narratorData?.firstName || '',
      lastName: quicktale.narratorData?.lastName || '',
      username: quicktale.narratorData?.userName || '',
    };
  }

  // Case 1b: narrator is a registered user id, but narratorData was not provided.
  // Fetch from users collection.
  if (quicktale?.narrator && typeof quicktale.narrator === 'string') {
    const narratorId = quicktale.narrator.trim();
    // In practice, narrator is either a custom narrator name OR a users/{id}.
    // Some environments may use non-20-char ids, so prefer an optimistic lookup.
    try {
      const userSnap = await getDoc(doc(db, 'users', narratorId));
      if (userSnap.exists()) {
        const user = userSnap.data();
        return {
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          username: user?.userName || '',
        };
      }
    } catch (e) {
      // If this fails due to permissions/etc, fall through to custom narrator handling.
      console.error('Failed to fetch narrator user doc:', e);
    }
  }

  // Case 2: narrator is a custom narrator (stored as a name string)
  if (quicktale?.narrator && quicktale?.createdBy) {
    try {
      // Try legacy user-scoped resolution first.
      // NOTE: quicktale.createdBy is not always the same identifier used in
      // customNarrators.createdBy (e.g. adminId vs userId normalization).
      let customNarrator = await getCustomNarratorByName(
        quicktale.narrator.trim(),
        quicktale.createdBy
      );

      // Fallback: org-scoped narrator sharing.
      if (!customNarrator && quicktale?.organization) {
        customNarrator = await getCustomNarratorByNameAndOrganization(
          quicktale.narrator.trim(),
          quicktale.organization
        );
      }

      return {
        firstName: customNarrator?.firstName || '',
        lastName: customNarrator?.lastName || '',
        username: quicktale.narrator,
      };
    } catch (e) {
      console.error('Failed to resolve custom narrator tokens:', e);
      return {
        firstName: '',
        lastName: '',
        username: quicktale.narrator,
      };
    }
  }

  return { firstName: '', lastName: '', username: '' };
};

const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
};

const buildGreetingHtml = ({ templateGreeting, tokens, quicktale }) => {
  if (!templateGreeting || typeof templateGreeting !== 'string') return '';

  // IMPORTANT: templateGreeting is expected to be HTML from ReactQuill.
  // Token values must be escaped to prevent HTML injection.
  const qtTitle = escapeHtml(quicktale?.title || '');
  const firstName = escapeHtml(tokens?.firstName || '');
  const lastName = escapeHtml(tokens?.lastName || '');
  const username = escapeHtml(tokens?.username || '');

  return templateGreeting
    .replaceAll('{First Name}', firstName)
    .replaceAll('{Last Name}', lastName)
    .replaceAll('{Username}', username)
    .replaceAll('{QuickTale Title}', qtTitle)
    .trim();
};

const getNarratorDisplayName = (quicktale) => {
  if (quicktale?.narratorData) {
    return `${quicktale.narratorData?.firstName || ''} ${quicktale.narratorData?.lastName || ''}`.trim();
  }
  return (quicktale?.narrator || '').trim();
};

const isNonEmptyString = (value) =>
  typeof value === 'string' && value.trim().length > 0;

// AddToQuickTale payloads can have photo in different shapes depending on source.
// - Firestore canonical shape: imageUrls: string[]
// - Some UI payloads also expose photoUrl for convenience
const getPrimaryPhotoUrl = (quicktale) => {
  if (!quicktale) return '';
  if (Array.isArray(quicktale.imageUrls) && isNonEmptyString(quicktale.imageUrls[0])) {
    return quicktale.imageUrls[0].trim();
  }
  if (isNonEmptyString(quicktale.photoUrl)) return quicktale.photoUrl.trim();
  if (isNonEmptyString(quicktale.imageUrl)) return quicktale.imageUrl.trim();
  return '';
};

/**
 * Safely load audio metadata and return duration in whole seconds.
 * Returns null if duration can't be determined.
 */
const getAudioDurationSeconds = (url, { timeoutMs = 15000 } = {}) => {
  return new Promise((resolve) => {
    try {
      const audio = new Audio(url);
      // Hint browser to only download metadata.
      audio.preload = 'metadata';
      // Allows metadata reads for CORS-enabled URLs.
      audio.crossOrigin = 'anonymous';

      const cleanup = () => {
        audio.onloadedmetadata = null;
        audio.onerror = null;
      };

      const timer = setTimeout(() => {
        cleanup();
        resolve(null);
      }, timeoutMs);

      audio.onloadedmetadata = () => {
        clearTimeout(timer);
        const duration = Math.floor(audio.duration);
        cleanup();
        resolve(Number.isFinite(duration) && duration > 0 ? duration : null);
      };

      audio.onerror = () => {
        clearTimeout(timer);
        cleanup();
        resolve(null);
      };
    } catch (e) {
      resolve(null);
    }
  });
};

/**
 * Get duration by downloading audio bytes and decoding locally.
 * This is more reliable than `new Audio(url).duration` for Firebase Storage URLs.
 */
const getAudioDurationSecondsFromUrlBytes = async (url) => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const arrayBuffer = await res.arrayBuffer();
    const audioCtx = new AudioContextClass();
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
    const duration = Math.floor(audioBuffer.duration);
    try {
      await audioCtx.close();
    } catch (e) {
      // noop
    }
    return Number.isFinite(duration) && duration > 0 ? duration : null;
  } catch (e) {
    return null;
  }
};

const AddToQuickTaleContainer = () => {
  const [searchParams] = useSearchParams();
  const [quicktaleDetails, setQuicktaleDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Org email template header branding
  const [orgLogoUrl, setOrgLogoUrl] = useState('');
  const [organizationName, setOrganizationName] = useState('');

  const [narratorControls, setNarratorControls] = useState({
    narratorCanUpdateAudio: true,
    narratorCanUpdatePhoto: false,
    narratorCanUpdateTitle: false,
    narratorCanUpdateDescription: false,
    narratorGreeting: '',
  });
  const [greetingHtml, setGreetingHtml] = useState('');
  const [narratorDisplayName, setNarratorDisplayName] = useState('');
  
  // Edit states
  const [uploadImage, setUploadImage] = useState(null);
  const [uploadAudioFile, setUploadAudioFile] = useState(null);
  const isSaving = useRef(false);
  // Last-saved title/description, used to report which fields actually changed.
  const savedTextFields = useRef({ title: '', description: '' });
  const [audioWasUpdated, setAudioWasUpdated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState('idle'); // 'idle' | 'submitting' | 'complete'

  // Modals
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [processedAudioModal, setProcessedAudioModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const shareId = searchParams.get('shareId');
      if (!shareId) {
        setError('No share identifier provided.');
        setLoading(false);
        return;
      }

      try {
        // Sign in anonymously to bypass security rules for reads/writes if needed
        const auth = getAuth();
        await signInAnonymously(auth);

        const getQuickTaleByShareId = httpsCallable(functions, 'getQuickTaleByShareId');
        const result = await getQuickTaleByShareId({ shareId });

        const qt = result.data;
        setQuicktaleDetails(qt);
        savedTextFields.current = {
          title: qt?.title || '',
          description: qt?.description || '',
        };
        // Initial display name (may be overridden below once we resolve tokens/user)
        setNarratorDisplayName(getNarratorDisplayName(qt));

        // Always resolve narrator tokens/display name (even if there is no template).
        // This covers the case where quicktale.narrator is a users/{id} but narratorData
        // wasn't included in the payload.
        const narratorTokens = await resolveNarratorTokens(qt);
        const resolvedName = `${narratorTokens?.firstName || ''} ${narratorTokens?.lastName || ''}`.trim();
        const resolvedDisplay = resolvedName || narratorTokens?.username || getNarratorDisplayName(qt);
        setNarratorDisplayName(resolvedDisplay);

        // Fetch template-driven narrator edit controls
        if (qt?.emailTemplateId) {
          try {
            const templateSnap = await getDoc(doc(OrgEmailTemplatesRef, qt.emailTemplateId));
            if (templateSnap.exists()) {
              const templateData = templateSnap.data();

              // Optional org branding
              setOrgLogoUrl(templateData?.logo || '');

              const normalized = getNarratorDefaultsFromTemplate(templateData);
              setNarratorControls(normalized);

              // narratorDisplayName already resolved above; no need to override here.
              setGreetingHtml(
                buildGreetingHtml({
                  templateGreeting: normalized.narratorGreeting,
                  tokens: narratorTokens,
                  quicktale: qt,
                })
              );
            } else {
              // Missing template doc => use defaults
              const normalized = getNarratorDefaultsFromTemplate(null);
              setNarratorControls(normalized);
              setGreetingHtml('');
              setOrgLogoUrl('');
            }
          } catch (e) {
            console.error('Failed to fetch email template for narrator controls:', e);
            const normalized = getNarratorDefaultsFromTemplate(null);
            setNarratorControls(normalized);
            setGreetingHtml('');
            setOrgLogoUrl('');
          }
        } else {
          const normalized = getNarratorDefaultsFromTemplate(null);
          setNarratorControls(normalized);
          setGreetingHtml('');
          setOrgLogoUrl('');
        }

      } catch (err) {
        setError(`Data fetch failed: ${err.message}`);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  useEffect(() => {
    const fetchOrganizationName = async () => {
      const orgId = quicktaleDetails?.organization;
      if (!orgId) {
        setOrganizationName('');
        return;
      }

      try {
        const orgSnap = await getSingleOrg(orgId);
        if (orgSnap?.exists()) {
          setOrganizationName(orgSnap.data()?.name || '');
        } else {
          setOrganizationName('');
        }
      } catch (e) {
        console.error('Failed to fetch organization name:', e);
        setOrganizationName('');
      }
    };

    fetchOrganizationName();
  }, [quicktaleDetails?.organization]);

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    setLoading(true);
    try {
      // Ensure anonymous auth is still valid before hitting Firebase Storage.
      await ensureFirebaseAuth();

      if (type === "original") {
        const audioUrl = await uploadQuickTaleAudioToStorage(file.raw);
        const dataTemp = {
          audioUrl: audioUrl,
          originalAudioName: file.raw.name,
        };

        // Prefer duration coming from recorder flow (computed from raw bytes).
        const durationFromCaller =
          typeof file?.durationSeconds === 'number' && Number.isFinite(file.durationSeconds)
            ? Math.floor(file.durationSeconds)
            : null;

        // Fallback to URL-based duration (may fail on some Firebase URLs)
        const duration = durationFromCaller ?? (await getAudioDurationSeconds(audioUrl));
        if (duration !== null) {
          dataTemp.audioLength = duration;
          console.log('Uploaded audioLength:', duration);
        } else {
          console.warn('Unable to determine uploaded audio duration for:', audioUrl);
        }

        setQuicktaleDetails((prev) => ({ ...prev, ...dataTemp }));
        
        setAudioWasUpdated(true);
        //showNotificationCenter("Audio uploaded successfully.");
      }
    } catch (error) {
      CenteredToast("error", `Error uploading file: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!quicktaleDetails) return;
    setLoading(true);
    setIsSubmitting(true);
    setSubmissionStatus('submitting');
    isSaving.current = true;
    const shareId = searchParams.get('shareId');

    try {
      // Ensure anonymous auth is still valid before any Storage/Firestore/Functions.
      await ensureFirebaseAuth();

      let dataTemp = {
        updatedAt: new Date(),
        shareId: '',
      };

      // Only include fields external narrator is allowed to edit
      if (narratorControls.narratorCanUpdateTitle) {
        dataTemp.title = quicktaleDetails.title;
      }
      if (narratorControls.narratorCanUpdateDescription) {
        dataTemp.description = quicktaleDetails.description;
      }

      if (uploadImage && narratorControls.narratorCanUpdatePhoto) {
        const photoUrl = await uploadQuickTaleImageToStorage(uploadImage.raw);
        dataTemp.imageUrls = [photoUrl];
        dataTemp.originalPhotoName = uploadImage.raw.name;

        // Keep UI in sync with persisted data. Otherwise when we clear `uploadImage`
        // after submit, the preview disappears and the new image never renders.
        setQuicktaleDetails((prev) => ({
          ...prev,
          imageUrls: [photoUrl],
          // Many UI surfaces still read photoUrl directly.
          photoUrl,
        }));
      }

      if (narratorControls.narratorCanUpdateAudio && quicktaleDetails.audioUrl) {
         dataTemp.audioUrl = quicktaleDetails.audioUrl;
         dataTemp.originalAudioName = quicktaleDetails.originalAudioName;
      }

      // Persist audioLength if we have a valid number.
      // NOTE: Don't rely on truthy checks (e.g. 0 is a valid value).
      if (
        narratorControls.narratorCanUpdateAudio &&
        typeof quicktaleDetails.audioLength === 'number' &&
        Number.isFinite(quicktaleDetails.audioLength)
      ) {
        dataTemp.audioLength = Math.floor(quicktaleDetails.audioLength);
        console.log('Saving audioLength:', dataTemp.audioLength);
      }

      // If title, audio, and photo are present after this save (regardless of edit permissions),
      // then clear draft state.
      // NOTE: We compute this from the final persisted values (existing + changes).
      const finalTitle = dataTemp.title ?? quicktaleDetails.title;
      const finalAudioUrl = dataTemp.audioUrl ?? quicktaleDetails.audioUrl;
      const finalPhotoUrl =
        (Array.isArray(dataTemp.imageUrls) && dataTemp.imageUrls[0])
          ? dataTemp.imageUrls[0]
          : getPrimaryPhotoUrl(quicktaleDetails);

      if (
        isNonEmptyString(finalTitle) &&
        isNonEmptyString(finalAudioUrl) &&
        isNonEmptyString(finalPhotoUrl)
      ) {
        dataTemp.isDraft = false;
      }

      // Use the correct update function based on which collection the data came from
      // (AddToQuickTale uses a server payload that includes `collection`)
      try {
        console.log('About to update Firestore:', {
          id: quicktaleDetails.id,
          collection: quicktaleDetails.collection,
          dataTemp,
        });
        if (quicktaleDetails.collection === 'batchQuicktales') {
          await updateBatchQuickTale(quicktaleDetails.id, dataTemp);
          console.log('Updated batchQuicktale:', quicktaleDetails.id);
        } else {
          await updateQuickTale(quicktaleDetails.id, dataTemp);
          console.log('Updated quicktale:', quicktaleDetails.id);
        }
      } catch (e) {
        console.error('Firestore update failed:', e);
        throw e;
      }
      
      // If audio was updated, fire-and-forget Auphonic processing.
      // V4 is collection-aware: it writes processedAudioUrl + audioLength back to
      // the correct Firestore collection (and also quicktales if already promoted)
      // entirely server-side — no need to await here.
      if (audioWasUpdated && narratorControls.narratorCanUpdateAudio) {
        // Ensure auth token is fresh before calling the function.
        ensureFirebaseAuth().then(() => {
          const processAudio = httpsCallable(functions, 'processAudioWithAuphonicV4');
          processAudio({
            fileUrl: quicktaleDetails.audioUrl,
            qtTitle: dataTemp.title,
            quicktaleId: quicktaleDetails.id,
            collection: quicktaleDetails.collection || 'quicktales',
          }).catch((err) => {
            // Non-fatal: audio processing failed in background.
            // The admin can re-process from the batch QT details page.
            console.error('Background Auphonic processing failed:', err.message);
          });
        }).catch((err) => {
          console.error('ensureFirebaseAuth failed before background Auphonic call:', err.message);
        });
      }

      // Which fields this save touched — used in the admin notification/email.
      const changes = [];
      if (audioWasUpdated && narratorControls.narratorCanUpdateAudio) changes.push('audio');
      if (Array.isArray(dataTemp.imageUrls)) changes.push('photo');
      if (
        narratorControls.narratorCanUpdateTitle &&
        (dataTemp.title || '') !== savedTextFields.current.title
      ) {
        changes.push('title');
      }
      if (
        narratorControls.narratorCanUpdateDescription &&
        (dataTemp.description || '') !== savedTextFields.current.description
      ) {
        changes.push('description');
      }
      savedTextFields.current = {
        title: dataTemp.title ?? savedTextFields.current.title,
        description: dataTemp.description ?? savedTextFields.current.description,
      };

      // Fire-and-forget: log share activity update via cloud function.
      // This updates the most recent shareActivity doc's updatedAt field and
      // creates a `quicktale_update` admin notification (in-app + email).
      if (shareId && quicktaleDetails?.id) {
        ensureFirebaseAuth().then(() => {
          const logShareActivityUpdate = httpsCallable(functions, 'logShareActivityUpdate');
          logShareActivityUpdate({
            quicktaleId: quicktaleDetails.id,
            collection: quicktaleDetails.collection || 'batchQuicktales',
            shareId,
            changes,
            narratorName: narratorDisplayName || '',
          }).catch((err) => {
            console.error('Non-fatal: logShareActivityUpdate failed:', err.message);
          });
        }).catch((err) => {
          console.error('ensureFirebaseAuth failed before logShareActivityUpdate:', err.message);
        });
      }

      showNotificationCenter('QuickTale updated successfully!');
      setSubmissionStatus('complete');
      
    } catch (err) {
      CenteredToast('error', `Failed to save changes: ${err.message}`);
      setSubmissionStatus('idle');
    } finally {
      setLoading(false);
      setIsSubmitting(false);
      isSaving.current = false;
      setAudioWasUpdated(false);
      setUploadImage(null);
    }
  };

  // Modal Handlers
  const handleImagePreviewModalOpen = () => setImagePreviewModal(true);
  const handleImagePreviewModalClose = () => setImagePreviewModal(false);
  const handleAudioModalOpen = () => setAudioModal(true);
  const handleAudioModalClose = () => setAudioModal(false);
  const handleProcessedAudioModalOpen = () => setProcessedAudioModal(true);
  const handleProcessedAudioModalClose = () => setProcessedAudioModal(false);

  if (loading && !quicktaleDetails) {
    return <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%' }} />;
  }

  if (error) {
    return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: 18, mt: 3 }}>
               Your link has expired.
          </Typography>
          <Typography sx={{ fontSize: 18, mt: 3 }}>
               For help or to request a new link, email{" "}
              <a href="mailto:support@quicktales.com" style={{ color: "#00A7CD" }}>
                support@quicktales.com
              </a>
              {" "}or call{" "}
              <a href="tel:+14242741055" style={{ color: "#00A7CD" }}>
                424-274-1055
              </a>.
          </Typography>
        </Box>
    );
  }

  if (!quicktaleDetails) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", padding: '0px 20px' }}>
      <CssBaseline />
      <Box sx={{ bgcolor: '#fff', p: 2, textAlign: 'center', zIndex: 1000 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          {orgLogoUrl ? (
            <img
              src={orgLogoUrl}
              alt="Organization Logo"
              style={{ height: 50, width: 'auto', objectFit: 'contain', marginRight: '12vw' }}
              onError={() => setOrgLogoUrl('')}
            />
          ) : null}
          <img src="/logoblue-270.png" alt="QuickTales Logo" style={{ height: 50 }} />
        </Box>

        <Box sx={{ mt: 5 }}>
          {organizationName ? (
            <Typography sx={{ fontWeight: 700, fontSize: 22 }}>
              {organizationName}
            </Typography>
          ) : null}
          <Typography sx={{ fontSize: 20 }}>
            QuickTale Audio Story Submission
          </Typography>
        </Box>
      </Box>

      <Grid container justifyContent="center" sx={{ flexGrow: 1, bgcolor: '#fff', pt: 4, pb: 4, mt: -4 }}>
        <Grid item xs={12} md={8} lg={6}>

            <ExternalNarratorDataFieldsSection
              quicktaleDetails={quicktaleDetails}
              setQuicktaleDetails={setQuicktaleDetails}
              isSaving={isSaving}
              greetingHtml={greetingHtml}
              narratorDisplayName={narratorDisplayName}
              submissionStatus={submissionStatus}
              canEditPhoto={submissionStatus === 'idle' && narratorControls.narratorCanUpdatePhoto}
              canEditAudio={submissionStatus === 'idle' && narratorControls.narratorCanUpdateAudio}
              canEditTitle={submissionStatus === 'idle' && narratorControls.narratorCanUpdateTitle}
              canEditDescription={submissionStatus === 'idle' && narratorControls.narratorCanUpdateDescription}
              uploadImage={uploadImage}
              setUploadImage={setUploadImage}
              uploadAudioFile={uploadAudioFile}
              setUploadAudioFile={setUploadAudioFile}
              handleFileUpload={handleFileUpload}
              onSubmit={handleSave}
              submitDisabled={
                loading ||
                !quicktaleDetails?.audioUrl ||
                !narratorControls.narratorCanUpdateAudio
              }
              submitLabel={
                isSubmitting
                  ? 'SUBMITTING...'
                  : loading
                    ? 'SAVING RECORDING...'
                    : 'SUBMIT'
              }
              handleImagePreviewModalOpen={handleImagePreviewModalOpen}
              handleAudioModalOpen={handleAudioModalOpen}
            />
        </Grid>
      </Grid>
      
      {/* Modals */}
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        image={uploadImage ? uploadImage.preview : getPrimaryPhotoUrl(quicktaleDetails)}
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        audioFile={
          uploadAudioFile != null && uploadAudioFile.preview
            ? uploadAudioFile.preview
            : quicktaleDetails.audioUrl
        }
      />
      <AudioModal
        modalOpen={processedAudioModal}
        handleClose={handleProcessedAudioModalClose}
        audioFile={quicktaleDetails.processedAudioUrl}
      />
    </Box>
  );
};

export default AddToQuickTaleContainer;
